import React, { useEffect, useState } from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import * as XLSX from "xlsx";
import html2canvas from "html2canvas";
import { useSearchParams } from "react-router";
import axiosClient from "../../axios-client";
ChartJS.register(ArcElement, Tooltip, Legend);

const PieChartSection = ({ segments }) => {
  const [searchParams] = useSearchParams();
  const region = searchParams.get("region"); // Gets the 'region' from URL
  const country = searchParams.get("country_name");
  const [segment, setSegment] = useState([]);
  useEffect(() => {
    if (country) {
      const filteredSegments = segments?.segments?.filter(
        (s) => s.country === country
      );
      setSegment(filteredSegments);
    }
    if (region) {
      const filteredSegments = segments?.segments?.filter(
        (s) => s.region === region
      );
      setSegment(filteredSegments);
    }
  }, [country]);

  const labels = segment[0]?.segments[0]?.subSegments?.map(
    (subSegment) => subSegment?.name
  );

  const generateColors = (count) => {
    return Array.from(
      { length: count },
      () => `#${Math.floor(Math.random() * 16777215).toString(16)}`
    );
  };

  // Generate colors dynamically based on number of labels
  const backgroundColors = generateColors(labels?.length);

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        display: false,
      },
    },
  };

  const downloadChartAsPNG = (index) => {
    const canvasContainer = document.getElementById(`chartContainer-${index}`);
    if (!canvasContainer) return; // Ensure the container exists
    addDownloads();
    html2canvas(canvasContainer).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const link = document.createElement("a");

      // Construct file name correctly
      const locationName = country || region || "Report";
      const reportTitle = segments?.reportTitle
        ? segments.reportTitle.replace(/Global\s+|\s+Size Study/g, "").trim()
        : "Chart";

      link.href = imgData;
      link.download = `${locationName}_${reportTitle}_chart.png`.replace(
        /\s+/g,
        "_"
      ); // Replace spaces with underscores
      link.click();
    });
  };
  const [subscriptions, setSubscriptions] = useState([]);
  const [userData, setUserData] = useState();
  const userID = localStorage.getItem("userID");
  const fetchSubscriptions = async () => {
    try {
      const response = await axiosClient.get(
        `/subscriptions/getSubscriptionsByUser/${userID}`
      );
      setSubscriptions(response.data);
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
    }
  };
  useEffect(() => {
    const plansData = sessionStorage.getItem("plans");
    if (plansData) {
      const parsedData = JSON.parse(plansData);
      setUserData(parsedData);
    }
    fetchSubscriptions();
  }, []);
  const addDownloads = async () => {
    const subscriptionId = subscriptions[0]?._id;
    try {
      const response = await axiosClient.patch(
        `/subscriptions/update-plan/${subscriptionId}`
      );
      fetchSubscriptions();
      console.log(response, "addDownloads");
    } catch (error) {
      console.log(error);
    }
  };
  return (
    <div className="py-8">
      {segment && (
        <div className="flex flex-wrap justify-center gap-10">
          {(() => {
            let filteredSegments = [];

            if (country) {
              // If country is selected, get its segments
              filteredSegments =
                segment.find((s) => s.country === country)?.segments || [];
            } else if (region) {
              // If region is selected, merge segments of all countries in the region
              const countriesInRegion = segment.filter(
                (s) => s.region === region
              );
              let mergedSegments = {};

              countriesInRegion.forEach((countryData) => {
                countryData.segments.forEach((seg) => {
                  if (!mergedSegments[seg.name]) {
                    mergedSegments[seg.name] = {
                      name: seg.name,
                      subSegments: [],
                    };
                  }

                  seg.subSegments.forEach((subSeg, i) => {
                    if (!mergedSegments[seg.name].subSegments[i]) {
                      mergedSegments[seg.name].subSegments[i] = {
                        name: subSeg.name,
                        years: subSeg.years.map((y) => ({
                          year: y.year,
                          value: 0,
                        })),
                      };
                    }

                    subSeg.years.forEach((yearData, j) => {
                      mergedSegments[seg.name].subSegments[i].years[j].value +=
                        yearData.value;
                    });
                  });
                });
              });

              filteredSegments = Object.values(mergedSegments);
            }

            return filteredSegments.map((segment, index) => {
              const subSegments = segment?.subSegments?.slice(0, -1); // Remove last subSegment
              const labels = subSegments?.map((subSegment) => subSegment?.name);

              const secondYearValues = subSegments?.map(
                (subSegment) => subSegment.years[1]?.value.toFixed(2) // 2025
              );
              const lastYearValues = subSegments?.map(
                (subSegment) =>
                  subSegment.years[subSegment.years.length - 1]?.value.toFixed(
                    2
                  ) // 2033
              );
              const secondYear = subSegments[0]?.years[1]?.year;
              const lastYear =
                subSegments[0]?.years[subSegments[0]?.years.length - 1]?.year;

              const colors = [
                "#889CEC",
                "#DD517C",
                "#B042AA",
                "#ED7845",
                "#CC4068",
                "#E96F52",
                "#7A8FE0",
                "#983D95",
                "#D94C72",
                "#D94C72",
              ];

              const data2025 = {
                labels,
                datasets: [
                  {
                    data: secondYearValues,
                    backgroundColor: colors,
                    hoverOffset: 4,
                  },
                ],
              };

              const data2033 = {
                labels,
                datasets: [
                  {
                    data: lastYearValues,
                    backgroundColor: colors,
                    hoverOffset: 4,
                  },
                ],
              };

              return (
                <div
                  key={index}
                  className="container mx-auto p-6 bg-white rounded-lg shadow border1 font-inter"
                >
                  <div className="mb-4 flex flex-col lg:flex-row items-center justify-between font-inter">
                    <h2 className="text-xl font-bold font4">
                      {country ? country : region}{" "}
                      {segments?.reportTitle?.replace(
                        /Global\s+|\s+Size Study/g,
                        ""
                      )}{" "}
                      Market, {segment?.name} {secondYear} & {lastYear}
                    </h2>
                    <div className="text-center">
                      {userData?.features?.graphDownloadLimit >
                        subscriptions[0]?.downloads?.downloadGraphs && (
                        <button
                          onClick={() => downloadChartAsPNG(index)}
                          className="bg-black text-white px-14 py-2 rounded-xl font-semibold hover:bg-[#21242a]"
                        >
                          Download
                        </button>
                      )}
                    </div>
                  </div>
                  <div
                    className="w-full flex  gap-10 flex-wrap"
                    id={`chartContainer-${index}`}
                    style={{ justifyContent: "center", gap: "18rem" }}
                  >
                    {/* First Pie Chart (2025) */}
                    <div className="w-1/5">
                      <div>
                        <h3 className="text-center font-medium font-inter text-2xl mb-4">
                          Year {secondYear}
                        </h3>
                        <Pie data={data2025} options={options} />
                      </div>
                      <div
                        className="mt-4 flex"
                        style={{
                          flexWrap: "wrap",
                          justifyContent: "space-between",
                        }}
                      >
                        {subSegments?.map((subSegment, i) => (
                          <div key={i} className="flex items-center gap-2 mb-2">
                            <div
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: colors[i] }}
                            ></div>
                            <span className="text-sm font-medium">
                              {subSegment?.name}: {secondYearValues[i]}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Second Pie Chart (2033) */}
                    <div className="w-1/5">
                      <h3 className="text-center font-medium font-inter text-2xl mb-4">
                        Year {lastYear}
                      </h3>
                      <Pie data={data2033} options={options} />
                      <div
                        className="mt-4 flex"
                        style={{
                          flexWrap: "wrap",
                          justifyContent: "space-between",
                        }}
                      >
                        {subSegments?.map((subSegment, i) => (
                          <div key={i} className="flex items-center gap-2 mb-2">
                            <div
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: colors[i] }}
                            ></div>
                            <span className="text-sm font-medium">
                              {subSegment?.name}: {lastYearValues[i]}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}
    </div>
  );
};

export default PieChartSection;
